/**
 * Table of Contents HTML tuned for Word (html-docx-js): tables instead of flexbox,
 * valid nesting, pt units, mso table hints.
 */
import { UAE_FDL_REPORT_FONT_FAMILY } from './reportCoverMetadataHtml';
import {
  buildGhgLabelBadgeHtml,
  buildGhgTocNumberBadgeHtml,
  GHG_WORD_EXPORT_DESIGN_TOKENS,
} from './ghgWordExportDesign';
const T = GHG_WORD_EXPORT_DESIGN_TOKENS;

export function escapeTocHtml(value) {
  if (value == null || value === '') return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** html-docx-js collapses normal spaces in table cells — nbsp keeps words separated in Word. */
export function preserveTocSpacesForWord(escapedText) {
  if (escapedText == null || escapedText === '') return '';
  return String(escapedText).replace(/ /g, '&#160;');
}

/**
 * Native Word TOC field for html-docx-js exports. Word recalculates page numbers when the user
 * updates fields (right-click → Update Field). Requires document headings mapped to Word outline
 * levels (e.g. GRI section h1 + page h2).
 *
 * Uses Word "complex field" spans (not mso-field-code paragraphs). html-docx-js embeds HTML via
 * altchunks; Word only recognizes TOC fields when begin/separator/end are span elements.
 *
 * @param {string} titleText - TOC page title (plain paragraph, not an outline heading)
 * @param {string} updateHint - Placeholder until fields are updated in Word
 * @param {{ headingLevels?: string }} [options] - default "1-2" (Heading 1–2)
 */
export function buildWordNativeTocFieldHtml(titleText, updateHint, options = {}) {
  const headingLevels = options.headingLevels || '1-2';
  const titleEscaped = escapeTocHtml(titleText);
  const hintEscaped = escapeTocHtml(updateHint);
  const fieldCode = `TOC \\o "${headingLevels}" \\h \\z \\u`;

  return `<div style="font-family:Arial,Helvetica,sans-serif;color:#1e293b;padding:0;">
<p style="font-size:22pt;font-weight:700;margin:0 0 10pt;color:#0f172a;font-family:Georgia,'Times New Roman',serif;line-height:1.15;mso-line-height-rule:exactly;">${titleEscaped}</p>
<p class="MsoToc1" style="margin:0;padding:0;font-size:11pt;line-height:1.15;mso-line-height-rule:exactly;"><span style="mso-element:field-begin"></span>${fieldCode}<span style="mso-element:field-separator"></span><span style="mso-no-proof:yes;color:#555555;font-style:italic;font-size:11pt;line-height:1.15;mso-line-height-rule:exactly;">${hintEscaped}</span><span style="mso-element:field-end"></span></p>
</div>`;
}

/** Word HTML document shell (namespaces + optional field refresh on open). */
export function buildWordMsoDocumentShell(innerBodyHtml, inlineCss = '') {
  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="UTF-8" />
<meta name="ProgId" content="Word.Document" />
<meta name="Generator" content="Microsoft Word" />
<meta name="Originator" content="Microsoft Word" />
<!--[if gte mso 9]><xml>
<w:WordDocument>
<w:View>Print</w:View>
<w:UpdateFieldsOnOpen/>
</w:WordDocument>
</xml><![endif]-->
<style type="text/css">
${inlineCss}
</style>
</head>
<body>
${innerBodyHtml}
</body>
</html>`;
}

/**
 * Dynamic TOC from report sections (ReportExportModal).
 * @param {Array<{ name: string, type?: string, pages?: Array<{ name: string }> }>} sections
 * @param {string} titleText
 */
export function buildExportModalTocHtml(sections, titleText = 'Table of Contents') {
  let body = '';
  (sections || []).forEach((section) => {
    if (section.type === 'ROOT' || section.type === 'TITLE' || section.type === 'CONTENTS') return;
    body += `<tr><td colspan="2" style="padding:10pt 0 5pt 0;font-size:12pt;font-weight:bold;color:#0f172a;border-bottom:solid 1pt #cbd5e1;mso-line-height-rule:exactly;">${escapeTocHtml(section.name)}</td></tr>`;
    (section.pages || []).forEach((page) => {
      body += `<tr><td style="width:16pt;font-size:1pt;line-height:1pt;">&#8203;</td><td style="padding:5pt 0 5pt 8pt;font-size:11pt;color:#334155;line-height:1.4;border-bottom:solid 1pt #f1f5f9;mso-line-height-rule:exactly;">&#8226; ${escapeTocHtml(page.name)}</td></tr>`;
    });
  });
  return `<div style="font-family:Arial,Helvetica,sans-serif;padding:10pt 12pt;">
<h2 style="font-size:18pt;font-weight:700;margin:0 0 14pt 0;color:#1e293b;line-height:1.2;mso-line-height-rule:exactly;">${escapeTocHtml(titleText)}</h2>
<table cellspacing="0" cellpadding="0" border="0" width="100%" style="border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;">${body}</table>
</div>`;
}

const ghgTocBadgeCellHtml = (num, bgColor, cellStyleExtra = '') => {
  const badgeHtml = buildGhgTocNumberBadgeHtml(num, bgColor);
  const badgeGap = T.tocBadgeTextGap;
  const badgeW = T.tocBadgeColWidth;
  return `<td class="ghg-toc-badge-cell" width="44" valign="top" style="width:${badgeW};max-width:${badgeW};min-width:${badgeW};vertical-align:top;padding:0 ${badgeGap} 0 0;mso-padding-alt:0 ${badgeGap} 0 0;border:none;mso-border-alt:none;${cellStyleExtra}">
<span class="ghg-toc-badge-wrap" style="display:block;width:${badgeW};height:${badgeW};line-height:0;font-size:0;mso-line-height-rule:exactly;">${badgeHtml}</span>
</td>`;
};

const ghgTocTextCellHtml = (titleEscaped, subtitleEscaped, cellStyleExtra = '') => {
  const textW = T.tocTextColWidth;
  const sub = subtitleEscaped
    ? `<p class="ghg-toc-subtitle" style="margin:3pt 0 0 0;padding:0;mso-margin-top-alt:3pt;mso-margin-bottom-alt:0;font-size:9pt;line-height:1.35;font-weight:400;color:#64748b;mso-line-height-rule:exactly;word-wrap:break-word;">${preserveTocSpacesForWord(subtitleEscaped)}</p>`
    : '';
  return `<td class="ghg-toc-text-cell" width="191" valign="top" style="width:${textW};max-width:${textW};vertical-align:top;padding:0;border:none;mso-border-alt:none;word-wrap:break-word;mso-line-height-rule:exactly;${cellStyleExtra}">
<p class="ghg-toc-title" style="margin:0;padding:0;mso-margin-top-alt:0;mso-margin-bottom-alt:0;font-size:11pt;font-weight:700;color:#0f172a;line-height:1.25;mso-line-height-rule:exactly;word-wrap:break-word;">${titleEscaped}</p>
${sub}
</td>`;
};

/**
 * @param {(key: string) => string} gt - already returns translated + escaped strings, or raw — caller passes escaped via gt
 */
export function buildGhgTableOfContentsHtml(gt) {
  const PRIMARY_BLUE = '#1d4ed8';
  const ACCENT_YELLOW = '#f59e0b';
  const MUTED_GREY = '#94a3b8';
  const DIVIDER = '#e2e8f0';
  const CARD_BORDER = '#e5e7eb';
  const TITLE_BLACK = '#0a0a0a';

  const items = [
    ['1', '#2563eb', 'GHGReport.ExecutiveSummary.label', 'GHGReport.OverviewHighlights.label'],
    ['2', '#059669', 'GHGReport.AboutReport.label', 'GHGReport.ReportBasisScope.label'],
    ['3', '#f59e0b', 'GHGReport.OrganizationalProfileBoundaries.label', 'GHGReport.OrganisationalStructureBoundaries.label'],
    ['4', '#06b6d4', 'GHGReport.BaseYearMethodology.label', 'GHGReport.ReportingBaseYearApproach.label'],
    ['5', '#ef4444', 'GHGReport.GHGEmissionsInventory.label', 'GHGReport.EmissionsAccounting.label'],
    ['6', '#8b5cf6', 'GHGReport.EnergyUseEmissionSources.label', 'GHGReport.EnergyMixEmissionSources.label'],
    ['7', '#f97316', 'GHGReport.GHGReductionStrategyPerformanceIndicators.label', 'GHGReport.GHGManagementPerformance.label'],
    ['8', '#14b8a6', 'GHGReport.VerificationAssuranceDataQuality.label', 'GHGReport.VerificationInventoryQuality.label'],
    ['9', '#d946ef', 'GHGReport.Insights.label', 'GHGReport.InsightsContent.label'],
    ['10', '#64748b', 'GHGReport.Annexures.label', 'GHGReport.ReferencesSupportingMaterials.label'],
  ];

  const gutter = T.tocGridColGutter;
  const rowPad = T.tocItemRowPaddingBottom;
  const colGap = gutter;

  const gridRows = items
    .slice(0, 5)
    .map((leftItem, rowIdx) => {
      const rightItem = items[rowIdx + 5];
      const [lNum, lColor, lTitle, lSub] = leftItem;
      const [rNum, rColor, rTitle, rSub] = rightItem;
      const isLastRow = rowIdx === 4;
      const padBottom = isLastRow ? '0' : rowPad;
      const sharedRowStyle =
        'border:none;border-left:none;border-right:none;border-top:none;border-bottom:none;mso-border-alt:none;mso-border-left-alt:none;mso-border-right-alt:none;mso-border-top-alt:none;mso-border-bottom-alt:none;';
      return `<tr class="ghg-toc-grid-row" style="vertical-align:top;mso-yfti-irow:${rowIdx};">
${ghgTocBadgeCellHtml(lNum, lColor, `padding-bottom:${padBottom};${sharedRowStyle}`)}
${ghgTocTextCellHtml(gt(lTitle), gt(lSub), `padding-right:${colGap};padding-bottom:${padBottom};${sharedRowStyle}`)}
${ghgTocBadgeCellHtml(rNum, rColor, `padding-bottom:${padBottom};${sharedRowStyle}`)}
${ghgTocTextCellHtml(gt(rTitle), gt(rSub), `padding-bottom:${padBottom};${sharedRowStyle}`)}
</tr>`;
    })
    .join('');

  return `<div class="ghg-word-toc" style="font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;background-color:#ffffff;width:${T.tocPageWidth};max-width:${T.tocPageWidth};margin:0 auto;">
<div class="ghg-toc-page-shell" style="padding:${T.tocShellPaddingWord};margin:0;width:${T.tocPageWidth};max-width:${T.tocPageWidth};background-color:#ffffff;border:1px solid ${CARD_BORDER};border-radius:12pt;-webkit-border-radius:12pt;-moz-border-radius:12pt;box-sizing:border-box;mso-line-height-rule:exactly;">

${buildGhgLabelBadgeHtml(gt('GHG.label'), PRIMARY_BLUE, 'toc')}
<h2 style="font-size:34pt;font-weight:800;margin:0;padding:0;color:${TITLE_BLACK};line-height:1.05;mso-line-height-rule:exactly;letter-spacing:-0.8pt;margin-bottom:30pt;">${gt('TableofContents.label')}</h2>

<table class="ghg-toc-page-layout-table ghg-toc-layout-table" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;min-height:${T.tocContentLayoutMinHeight};border:none;mso-border-alt:none;mso-table-lspace:0pt;mso-table-rspace:0pt;">
<tr><td class="ghg-toc-footer-content-cell" valign="top" style="vertical-align:top;padding:0;border:none;mso-border-alt:none;">
<table class="ghg-toc-grid-table ghg-toc-layout-table ghg-toc-flat-grid" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:100%;border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;border:none;mso-border-alt:none;table-layout:fixed;mso-table-layout-alt:fixed;">
<colgroup>
<col class="ghg-toc-col-badge" width="44" style="width:${T.tocBadgeColWidth};mso-width-source:userset;" />
<col class="ghg-toc-col-text" width="191" style="width:${T.tocTextColWidth};mso-width-source:userset;" />
<col class="ghg-toc-col-badge" width="44" style="width:${T.tocBadgeColWidth};mso-width-source:userset;" />
<col class="ghg-toc-col-text" width="191" style="width:${T.tocTextColWidth};mso-width-source:userset;" />
</colgroup>
${gridRows}
</table>
</td></tr>
<tr><td class="ghg-toc-footer-gap-cell" style="height:${T.tocFooterContentGap};line-height:${T.tocFooterContentGap};font-size:1pt;padding:0;border:none;mso-line-height-rule:exactly;mso-height-rule:exactly;">&nbsp;</td></tr>
<tr><td class="ghg-toc-footer-spacer-cell" style="height:${T.tocFooterSpacerHeight};line-height:1pt;font-size:1pt;padding:0;border:none;mso-line-height-rule:exactly;mso-height-rule:exactly;">&nbsp;</td></tr>
<tr><td class="ghg-toc-footer-bar-cell" valign="bottom" style="vertical-align:bottom;padding:0;border:none;mso-border-alt:none;">
<table class="ghg-toc-footer-bar ghg-toc-layout-table" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;border-collapse:collapse;margin:0;background-image:linear-gradient(90deg, ${PRIMARY_BLUE} 0%, ${PRIMARY_BLUE} 55%, ${ACCENT_YELLOW} 70%, ${ACCENT_YELLOW} 100%);">
<tr>
<td bgcolor="${PRIMARY_BLUE}" style="width:60%;height:3pt;line-height:3pt;font-size:1pt;background-color:${PRIMARY_BLUE};mso-line-height-rule:exactly;">&nbsp;</td>
<td bgcolor="${ACCENT_YELLOW}" style="width:40%;height:3pt;line-height:3pt;font-size:1pt;background-color:${ACCENT_YELLOW};mso-line-height-rule:exactly;">&nbsp;</td>
</tr>
</table>
</td></tr>
</table>
</div>
</div>`;
}

/**
 * Legacy TOC HTML (3-column grid, loose badge cells) → Word-safe 2-column layout.
 * @param {Document|ParentNode} doc
 */
/**
 * Unwrap nested ghg-toc-row-table (2-col) inside grid → flat 4-col rows for Word/html-docx-js.
 * @param {Document|ParentNode} doc
 */
export function flattenGhgTocNestedTablesInDocument(doc) {
  if (!doc?.querySelectorAll) return;

  doc.querySelectorAll('.ghg-word-toc table.ghg-toc-grid-table').forEach((grid) => {
    if (grid.classList.contains('ghg-toc-flat-grid')) {
      const firstRow = grid.querySelector('tr');
      if (firstRow && firstRow.querySelectorAll(':scope > td.ghg-toc-badge-cell').length >= 2) {
        return;
      }
    }

    const rows = grid.querySelectorAll(':scope > tbody > tr, :scope > tr');
    rows.forEach((tr) => {
      if (tr.querySelector('table.ghg-toc-row-table')) {
        const pairCells = [...tr.children].filter((el) => String(el.tagName).toLowerCase() === 'td');
        const flatCells = [];
        pairCells.forEach((outerTd, pairIdx) => {
          const rowTable = outerTd.querySelector('table.ghg-toc-row-table');
          if (!rowTable) return;
          const innerTds = [...rowTable.querySelectorAll('tr')].flatMap((r) =>
            [...r.children].filter((el) => String(el.tagName).toLowerCase() === 'td'),
          );
          if (innerTds.length < 2) return;
          const badgeTd = innerTds[0].cloneNode(true);
          const textTd = innerTds[innerTds.length - 1].cloneNode(true);
          if (pairIdx === 1) {
            const gutter = T.tocGridColGutter;
            textTd.setAttribute(
              'style',
              `${textTd.getAttribute('style') || ''};padding-left:0;`.replace(/;;/g, ';'),
            );
            const prevText = flatCells[flatCells.length - 1];
            if (prevText?.classList?.contains('ghg-toc-text-cell')) {
              prevText.setAttribute(
                'style',
                `${prevText.getAttribute('style') || ''};padding-right:${gutter};`.replace(/;;/g, ';'),
              );
            }
          }
          flatCells.push(badgeTd, textTd);
        });
        if (flatCells.length === 4) {
          tr.innerHTML = '';
          tr.classList.add('ghg-toc-grid-row');
          flatCells.forEach((cell) => tr.appendChild(cell));
        }
        return;
      }

      const itemCells = [...tr.children].filter(
        (el) =>
          String(el.tagName).toLowerCase() === 'td'
          && (el.classList.contains('ghg-toc-item-cell') || el.querySelector('.ghg-toc-row-table')),
      );
      if (itemCells.length === 2 && itemCells[0].querySelector('.ghg-toc-row-table')) {
        const flatCells = [];
        itemCells.forEach((outerTd, pairIdx) => {
          const rowTable = outerTd.querySelector('table.ghg-toc-row-table');
          if (!rowTable) return;
          const innerTds = [...rowTable.querySelectorAll('tr')].flatMap((r) =>
            [...r.children].filter((el) => String(el.tagName).toLowerCase() === 'td'),
          );
          if (innerTds.length < 2) return;
          const badgeTd = innerTds[0].cloneNode(true);
          const textTd = innerTds[innerTds.length - 1].cloneNode(true);
          if (pairIdx === 1) {
            const gutter = T.tocGridColGutter;
            textTd.setAttribute(
              'style',
              `${textTd.getAttribute('style') || ''};padding-left:0;`.replace(/;;/g, ';'),
            );
            const prevText = flatCells[flatCells.length - 1];
            if (prevText?.classList?.contains('ghg-toc-text-cell')) {
              prevText.setAttribute(
                'style',
                `${prevText.getAttribute('style') || ''};padding-right:${gutter};`.replace(/;;/g, ';'),
              );
            }
          }
          flatCells.push(badgeTd, textTd);
        });
        if (flatCells.length === 4) {
          tr.innerHTML = '';
          tr.classList.add('ghg-toc-grid-row');
          flatCells.forEach((cell) => tr.appendChild(cell));
        }
      }
    });

    grid.classList.add('ghg-toc-flat-grid');
  });
}

export function migrateGhgTocLayoutInDocument(doc) {
  if (!doc?.querySelectorAll) return;
  flattenGhgTocNestedTablesInDocument(doc);

  doc.querySelectorAll('.ghg-word-toc').forEach((toc) => {
    toc.querySelectorAll('table').forEach((table) => {
      if (table.classList.contains('ghg-toc-row-table')) return;
      const tr = table.querySelector(':scope > tbody > tr') || table.querySelector(':scope > tr');
      if (!tr) return;
      let tds = [...tr.children].filter((el) => String(el.tagName).toLowerCase() === 'td');
      if (tds.length !== 3) return;
      const mid = tds[1];
      const midStyle = (mid.getAttribute('style') || '').toLowerCase();
      const isSpacer =
        !((mid.textContent || '').replace(/\u00a0|\s/g, ''))
        || /width:\s*4%|font-size:\s*1pt/i.test(midStyle);
      if (!isSpacer) return;
      const cls = (table.getAttribute('class') || '').split(/\s+/).filter(Boolean);
      if (!cls.includes('ghg-toc-grid-table')) cls.push('ghg-toc-grid-table');
      if (!cls.includes('ghg-toc-layout-table')) cls.push('ghg-toc-layout-table');
      table.setAttribute('class', cls.join(' '));
      mid.remove();
      tds = [...tr.children].filter((el) => String(el.tagName).toLowerCase() === 'td');
      if (tds.length === 2) {
        const gutter = T.tocGridColGutter;
        tds[0].setAttribute('width', '50%');
        tds[1].setAttribute('width', '50%');
        tds[0].classList.add('ghg-toc-cell');
        tds[1].classList.add('ghg-toc-cell');
        tds[0].setAttribute(
          'style',
          `width:50%;vertical-align:top;padding:0 ${gutter} 0 0;border:none;mso-border-alt:none;`,
        );
        tds[1].setAttribute(
          'style',
          `width:50%;vertical-align:top;padding:0 0 0 ${gutter};border:none;mso-border-alt:none;`,
        );
      }
    });

    toc.querySelectorAll('table.ghg-toc-row-table tr').forEach((row) => {
      const cells = [...row.children].filter((el) => String(el.tagName).toLowerCase() === 'td');
      if (cells.length < 2) return;
      if (!cells[0].classList.contains('ghg-toc-badge-cell')) {
        cells[0].classList.add('ghg-toc-badge-cell');
        cells[0].setAttribute('width', '44');
      }
      if (!cells[cells.length - 1].classList.contains('ghg-toc-text-cell')) {
        cells[cells.length - 1].classList.add('ghg-toc-text-cell');
      }
    });
  });
}

/**
 * Stacked blocks with icon image in colored square (ESRS / VSME style TOC).
 * @param {string} titleEscaped
 * @param {Array<{ bg: string, iconSrc: string, title: string, items: string[] }>} blocks — title/items pre-escaped
 */
export function buildIconGridTocHtml(titleEscaped, blocks) {
  const rows = blocks
    .map((b) => {
      const lis = b.items.map((t) => `<li style="margin:3pt 0;font-size:10pt;line-height:1.35;color:#334155;mso-line-height-rule:exactly;">${t}</li>`).join('');
      const img = b.iconSrc
        ? `<img src="${b.iconSrc}" alt="" width="28" height="28" style="width:28px;height:28px;display:block;filter:brightness(0) invert(1);"/>`
        : '';
      return `<tr><td style="padding:10pt 0;border-bottom:solid 1pt #f1f5f9;">
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr>
<td style="width:48px;vertical-align:top;padding-right:10px;">
<table width="40" height="40" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;background-color:${b.bg};"><tr><td align="center" valign="middle" style="padding:4pt;">${img}</td></tr></table>
</td>
<td valign="top">
<p style="margin:0 0 6pt 0;font-size:11pt;font-weight:600;color:#0f172a;line-height:1.2;mso-line-height-rule:exactly;">${b.title}</p>
<ul style="margin:0;padding-left:20px;list-style-type:disc;">${lis}</ul>
</td>
</tr></table></td></tr>`;
    })
    .join('');
  return `<div style="margin:0;background-color:#fff;">
<div style="padding:10pt 12pt;">
<h2 style="font-size:16pt;font-weight:600;margin:0 0 16pt 0;color:#0f172a;line-height:1.2;mso-line-height-rule:exactly;">${titleEscaped}</h2>
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;">${rows}</table>
</div></div>`;
}

export const UAE_FDL_TOC_ENTRIES = [
  { num: '1', bg: '#1d4ed8', sectionKey: 'UAEFDLReport.AboutTheOrganization.label', pageKey: 'UAEFDLReport.AboutTheOrganization.label' },
  { num: '2', bg: '#2563eb', sectionKey: 'UAEFDLReport.AboutTheReport.label', pageKey: 'UAEFDLReport.ObjectivesOfDecreeLaw.label' },
  { num: '3', bg: '#059669', sectionKey: 'UAEFDLReport.InventoryOfGHGEmissions.label', pageKey: 'UAEFDLReport.GHGEmissionsInventory.label' },
  { num: '4', bg: '#0d9488', sectionKey: 'UAEFDLReport.InformationAboutEmissions.label', pageKey: 'UAEFDLReport.InformationAboutEmissions.label' },
  { num: '5', bg: '#d97706', sectionKey: 'UAEFDLReport.DecarbonizationSection.label', pageKey: 'UAEFDLReport.DecarbonizationTargetsStrategy.label' },
  { num: '6', bg: '#64748b', sectionKey: 'UAEFDLReport.VerificationSection.label', pageKey: 'UAEFDLReport.VerificationCompliance.label' },
  { num: '7', bg: '#7c3aed', sectionKey: 'UAEFDLReport.Insights.label', pageKey: 'UAEFDLReport.Insights.label' },
];

/**
 * UAE-FDL TOC row: rounded badge + section title + page subtitle.
 */
function uaeFdlTocRow(num, bgColor, titleEscaped, subtitleEscaped) {
  const sub = subtitleEscaped
    ? `<p style="margin:6pt 0 0 0;padding:0;font-size:10.5pt;line-height:1.45;font-weight:400;color:#64748b;mso-line-height-rule:exactly;">${subtitleEscaped}</p>`
    : '';
  return `<table class="uae-fdl-toc-row-table" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100% !important;border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;table-layout:fixed;border:none;mso-border-alt:none;"><tr>
<td style="width:54pt;vertical-align:top;padding:0 14pt 0 0;border:none;mso-border-alt:none;">
<table width="44" cellpadding="0" cellspacing="0" border="0" style="width:44pt !important;height:44pt !important;border-collapse:collapse;border:none;mso-border-alt:none;"><tr><td align="center" valign="middle" style="background-color:${bgColor};color:#ffffff;font-weight:700;font-size:13pt;mso-line-height-rule:exactly;padding:0;line-height:1;border-radius:10px;">${num}</td></tr></table>
</td>
<td valign="top" style="border:none;mso-border-alt:none;padding:2pt 0 0 0;border-bottom:1px solid #f1f5f9;">
<p style="margin:0;padding:0;font-size:12.5pt;font-weight:700;color:#0f172a;line-height:1.3;mso-line-height-rule:exactly;">${titleEscaped}</p>
${sub}
</td>
</tr></table>`;
}

/**
 * UAE-FDL report TOC — matches cover page styling (clean white, numbered badges).
 * @param {(key: string) => string} gt - translated + escaped strings
 */
export function buildUaeFdlTableOfContentsHtml(gt) {
  const rows = UAE_FDL_TOC_ENTRIES.map((entry) =>
    uaeFdlTocRow(entry.num, entry.bg, gt(entry.sectionKey), gt(entry.pageKey)),
  )
    .map(
      (inner) =>
        `<tr><td valign="top" style="padding:0 0 16pt 0;vertical-align:top;border:none;mso-border-alt:none;">${inner}</td></tr>`,
    )
    .join('');

  const titleDivider =
    `<table cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;margin:0 0 24pt 0;mso-table-lspace:0pt;mso-table-rspace:0pt;">` +
    `<tr>` +
    `<td style="height:4px;background-color:#1d4ed8;font-size:0;line-height:0;width:68%;">&nbsp;</td>` +
    `<td style="height:4px;background-color:#d4a017;font-size:0;line-height:0;width:32%;">&nbsp;</td>` +
    `</tr>` +
    `</table>`;

  return `<div class="uae-fdl-word-toc" style="font-family:${UAE_FDL_REPORT_FONT_FAMILY};page-break-after:always;">
<div style="padding:40pt 36pt 36pt 36pt;width:8.27in;min-height:11.69in;margin:auto;box-sizing:border-box;background:linear-gradient(160deg,#ffffff 0%,#f8fafc 48%,#ffffff 100%);border:1px solid #dbe3ee;border-radius:8px;mso-line-height-rule:exactly;">
<h2 style="font-size:22pt;font-weight:800;margin:0 0 14pt 0;color:#1d4ed8;line-height:1.15;letter-spacing:-0.3px;mso-line-height-rule:exactly;">${gt('TableofContents.label')}</h2>
${titleDivider}
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100% !important;border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;border:none;mso-border-alt:none;table-layout:fixed;">${rows}</table>
</div>
</div>`;
}
